import { inject, observer } from "mobx-react";
import React, { useState, useContext, useMemo, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useToast,
} from "randmarcomps";
import { Product as ApiProduct, usePutV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuInstantRebateMutation } from "randmar-api-client";
import ManufacturerContext from "../../contexts/ManufacturerContext";
import { Product as LegacyProduct } from "../../stores/product/Product";
import { RootStore } from "../../stores/rootStore";
import { getDefaultEndDate, getDefaultStartDate, setDefaultEndDate, setDefaultStartDate } from "../../utils/LocalStorageUtils";
import CenteredLoader from "../util/CenteredLoader";

interface IProps {
  store?: RootStore;
  product: LegacyProduct | ApiProduct;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getProductRandmarSku = (product: LegacyProduct | ApiProduct) => {
  if ("randmarSKU" in product && typeof product.randmarSKU === "string") {
    return product.randmarSKU;
  }
  if ("RandmarSKU" in product && typeof product.RandmarSKU === "string") {
    return product.RandmarSKU;
  }
  return undefined;
};

const getProductTitle = (product: LegacyProduct | ApiProduct) => {
  if ("title" in product && typeof product.title === "string") {
    return product.title;
  }
  if ("Title" in product && typeof product.Title === "string") {
    return product.Title;
  }
  return undefined;
};

const AddInstantRebate: React.FC<IProps> = ({ store, product, open, onOpenChange }) => {
  const { applicationId } = useContext(ManufacturerContext);
  const [rebate, setRebate] = useState<number | undefined>();
  const [dateStart, setDateStart] = useState<string | undefined>(getDefaultStartDate());
  const [dateEnd, setDateEnd] = useState<string | undefined>(getDefaultEndDate());
  const [promotionId, setPromotionId] = useState<string | undefined>();
  const { toast } = useToast();

  const [addInstantRebate, { isLoading }] = usePutV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuInstantRebateMutation();

  const randmarSKU = useMemo(() => getProductRandmarSku(product), [product]);
  const title = useMemo(() => getProductTitle(product), [product]);

  useEffect(() => {
    if (open) {
      setRebate(undefined);
      setDateStart(getDefaultStartDate());
      setDateEnd(getDefaultEndDate());
      setPromotionId(undefined);
    }
  }, [open]);

  const handleCancel = () => {
    if (isLoading) return;
    onOpenChange(false);
  };

  const handleConfirmRequest = async () => {
    const { productStore } = store!;

    try {
      await addInstantRebate({
        routeManufacturerId: applicationId,
        randmarSku: randmarSKU || "",
        instantRebateInput: {
          Rebate: rebate,
          StartDate: dateStart,
          EndDate: dateEnd,
          PromotionId: promotionId,
        },
      }).unwrap();

      toast({ variant: "success", title: "Success", description: `Instant rebate of $${rebate} successfully made.` });
      onOpenChange(false);
      productStore.getProduct(applicationId, randmarSKU || "");
    } catch (error) {
      console.error("Failed to create instant rebate:", error);
      toast({ variant: "destructive", title: "Error", description: "Could not create an instant rebate." });
    }
  };

  const handleDateStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDefaultStartDate(newValue);
    setDateStart(newValue);
  };

  const handleDateEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDefaultEndDate(newValue);
    setDateEnd(newValue);
  };

  const handlePromoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromotionId(e.target.value);
  };

  const handleRebateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRebate(Number.parseFloat(e.target.value));
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add instant rebate for {title || "this product"}</DialogTitle>
          <DialogDescription>
            Please fill in the details for the instant rebate below.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <CenteredLoader text="Please wait…" variant="plain" className="min-h-[240px]" />
        ) : (
          <>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="rebate" className="text-right">Rebate</label>
                <Input
                  id="rebate"
                  value={rebate ?? ""}
                  type="number"
                  min={0}
                  onChange={handleRebateChange}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="dateStart" className="text-right">Start Date</label>
                <Input
                  id="dateStart"
                  type="date"
                  value={dateStart}
                  onChange={handleDateStartChange}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="dateEnd" className="text-right">End Date</label>
                <Input
                  id="dateEnd"
                  type="date"
                  value={dateEnd}
                  onChange={handleDateEndChange}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="promoID" className="text-right">Promotion ID</label>
                <Input
                  id="promoID"
                  type="text"
                  value={promotionId ?? ""}
                  onChange={handlePromoChange}
                  className="col-span-3"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCancel}>Cancel</Button>
              <Button
                onClick={handleConfirmRequest}
                disabled={isLoading || !rebate || !dateStart || !dateEnd || !promotionId || dateStart > dateEnd}
              >
                Confirm
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default inject("store")(observer(AddInstantRebate));
